import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { LANGUAGES } from '@/language/languages';
import { loadTranslations } from '@/lib/i18nLoader';
import {
  getGeneralPrefs,
  setGeneralPrefs,
} from '@/lib/preferences';

export { LANGUAGES };

interface Translations {
  nav: Record<string, string>;
  status: Record<string, string>;
  alarms: Record<string, string>;
}

export interface I18nContextValue {
  locale: string;
  setLocale: (code: string) => void;
  t: (key: string, fallback?: string) => string;
  dir: string;
}

const I18nContext =
  createContext<I18nContextValue | null>(null);

export { I18nContext };

const DEFAULT_LOCALE = 'es';
const LOCALE_STORAGE_KEY = 'mixok-locale';

function getInitialLocale(): string {
  try {
    const savedLocale =
      localStorage.getItem(
        LOCALE_STORAGE_KEY,
      );

    if (savedLocale) {
      return savedLocale;
    }

    return DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
}

function lookup(
  translations: Translations | null,
  key: string,
  fallback?: string,
): string {
  if (!translations) {
    return fallback ?? key;
  }

  for (
    const section of [
      'nav',
      'status',
      'alarms',
    ] as const
  ) {
    const val =
      translations[section]?.[key];

    if (val != null) {
      return String(val);
    }
  }

  return fallback ?? key;
}

export function I18nProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [
    locale,
    setLocaleState,
  ] = useState(
    getInitialLocale,
  );

  const [
    translations,
    setTranslations,
  ] = useState<Translations | null>(
    null,
  );

  const [
    loadedLocale,
    setLoadedLocale,
  ] = useState<string>(
    DEFAULT_LOCALE,
  );

  const loadingRef =
    useRef<Promise<void> | null>(
      null,
    );

  const prefsRestoredRef =
    useRef(false);

  // On mount, restore the user's previously selected locale
  // from IndexedDB. If none exists, Spanish remains the default.
  useEffect(() => {
    if (
      prefsRestoredRef.current
    ) {
      return;
    }

    prefsRestoredRef.current =
      true;

    getGeneralPrefs().then(
      (prefs) => {
        if (
          prefs.locale &&
          prefs.locale !== locale
        ) {
          setLocaleState(
            prefs.locale,
          );

          try {
            localStorage.setItem(
              LOCALE_STORAGE_KEY,
              prefs.locale,
            );
          } catch {
            /* ignore */
          }
        }
      },
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load translations whenever locale changes
  useEffect(() => {
    let cancelled = false;

    // Reset loadedLocale so t() knows old translations are stale
    setLoadedLocale('');

    loadingRef.current =
      loadTranslations(
        locale,
      ).then((t) => {
        if (!cancelled) {
          setTranslations(t);
          setLoadedLocale(
            locale,
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [locale]);

  const setLocale =
    useCallback(
      (code: string) => {
        setLocaleState(code);

        try {
          localStorage.setItem(
            LOCALE_STORAGE_KEY,
            code,
          );
        } catch {
          /* ignore */
        }

        // Also persist to IndexedDB for durability
        // (merge with existing prefs)
        getGeneralPrefs().then(
          (existing) => {
            setGeneralPrefs({
              ...existing,
              locale: code,
            });
          },
        );
      },
      [],
    );

  const t = useCallback(
    (
      key: string,
      fallback?: string,
    ): string => {
      // If translations are from a different locale
      // (stale), don't use them.
      const currentTranslations =
        loadedLocale === locale
          ? translations
          : null;

      const result =
        lookup(
          currentTranslations,
          key,
        );

      // If not found in current locale, try English
      // as a compatibility fallback.
      if (
        result === key &&
        locale !== 'en'
      ) {
        loadTranslations(
          'en',
        ).then((en) => {
          setTranslations(
            (prev) =>
              prev ?? en,
          );
        });
      }

      return (
        result === key &&
        locale !== 'en'
          ? fallback ?? key
          : result
      );
    },
    [
      translations,
      loadedLocale,
      locale,
    ],
  );

  return (
    <I18nContext.Provider
      value={{
        locale,
        setLocale,
        t,
        dir:
          locale === 'ar' ||
          locale === 'fa'
            ? 'rtl'
            : 'ltr',
      }}
    >
      {children}
    </I18nContext.Provider>
  );
}

export function useT(): I18nContextValue {
  const ctx =
    useContext(
      I18nContext,
    );

  if (!ctx) {
    throw new Error(
      'useT must be used inside I18nProvider',
    );
  }

  return ctx;
}
